import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import { auth, db } from "../firebase";

type PuzzleStep = {
  imageUrl?: string;
  answer?: string;
  answerCandidates?: string[];
  answerFormat?: string;
  hint?: string;
};

type Puzzle = PuzzleStep & {
  id: string;
  title: string;
  level?: number;
  isVisible?: boolean;
  completionType?: "single" | "multiple";
  steps?: PuzzleStep[];
};

const pageBackground = "#f4f7fb";
const surfaceColor = "rgba(255,255,255,0.96)";
const cardBorder = "1px solid #d8e1eb";
const accentColor = "#173b57";
const goldColor = "#f5c542";

function SiteHeader({
  activeFilter,
  onFilterChange,
  searchText,
  onSearchChange,
  user,
  onLogin,
  onLogout,
  clearFilter,
  bookmarkFilter,
  onClearFilterChange,
  onBookmarkFilterChange,
  onRandomUncleared,
  onOpenNumberList,
}: {
  activeFilter: number | "latest";
  onFilterChange: (filter: number | "latest") => void;
  searchText: string;
  onSearchChange: (value: string) => void;
  user: User | null;
  onLogin: () => void;
  onLogout: () => void;
  clearFilter: "all" | "uncleared" | "cleared";
  bookmarkFilter: "all" | "bookmarked";
  onClearFilterChange: (filter: "all" | "uncleared" | "cleared") => void;
  onBookmarkFilterChange: (filter: "all" | "bookmarked") => void;
  onRandomUncleared: () => void;
  onOpenNumberList: () => void;
}) {
  return (
    <header
      style={{
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(135deg, #081521 0%, #10283d 34%, #173b57 68%, #245c86 100%)",
        color: "white",
        padding: "clamp(20px, 5vw, 30px) clamp(16px, 4vw, 28px) clamp(18px, 4vw, 24px)",
        borderRadius: 28,
        marginBottom: 24,
        boxShadow: "0 24px 54px rgba(8,21,33,0.22)",
        border: "1px solid rgba(255,255,255,0.10)",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at top right, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 34%), radial-gradient(circle at bottom left, rgba(245,197,66,0.12) 0%, rgba(245,197,66,0) 28%)",
          pointerEvents: "none",
        }}
      />

      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 22 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
              minWidth: 0,
              flex: "1 1 320px",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 18,
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.14)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)",
                backdropFilter: "blur(8px)",
                flexShrink: 0,
              }}
            >
              <img
                src="/logo.png"
                alt="dachou logo"
                style={{ width: 40, height: 40, objectFit: "contain" }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: "1 1 220px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "clamp(26px, 6vw, 34px)",
                  lineHeight: 1.08,
                  letterSpacing: "0.03em",
                  fontWeight: 800,
                }}
              >
                dachouの謎解き集
              </h1>
              <div style={{ fontSize: 13, opacity: 0.84, lineHeight: 1.7, wordBreak: "break-word" }}>
                好きな難易度で探して、解いて、記録を残せる謎解きサイト
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "flex-start",
              flex: "1 1 240px",
            }}
          >
            {user ? (
              <>
                <span
                  style={{
                    fontSize: 12,
                    background: "rgba(255,255,255,0.12)",
                    padding: "9px 12px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.14)",
                    backdropFilter: "blur(8px)",
                    fontWeight: 700,
                  }}
                >
                  ログイン中: {user.displayName ?? user.email ?? "ユーザー"}
                </span>
                <button
                  style={headerChipStyle("rgba(255,255,255,0.96)", "#173b57", false)}
                  onClick={onLogout}
                >
                  ログアウト
                </button>
                <a
                 href="https://x.com/dachou_nazo"
                 target="_blank"
                 rel="noopener noreferrer"
                 aria-label="Xを開く"
                 style={{
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   width: 40,
                   height: 40,
                   borderRadius: 999,
                   background: "#111827",
                   border: "1px solid rgba(255,255,255,0.2)",
                   boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                   flexShrink: 0,
                 }}
                >
                <img
                  src="/logo-white.png?v=2"
                  alt="X"
                  style={{ width: 20, height: 20, objectFit: "contain" }}
                />
              </a>
              </>
            ) : (
              <button
                style={headerChipStyle("rgba(255,255,255,0.96)", "#173b57", false)}
                onClick={onLogin}
              >
                Googleログイン
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
            alignItems: "stretch",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 22,
              padding: 14,
              backdropFilter: "blur(10px)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <input
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="問題番号・タイトルで検索"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.22)",
                fontSize: 14,
                background: "rgba(255,255,255,0.96)",
                color: "#111827",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div
            style={{
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 22,
              padding: 14,
              backdropFilter: "blur(10px)",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                onClick={() => onClearFilterChange("all")}
                style={headerChipStyle("#ffffff", "#173b57", clearFilter === "all")}
              >
                すべて
              </button>
              <button
                onClick={() => onClearFilterChange("uncleared")}
                style={headerChipStyle("#ffffff", "#173b57", clearFilter === "uncleared")}
              >
                未クリア
              </button>
              <button
                onClick={() => onClearFilterChange("cleared")}
                style={headerChipStyle("#ffffff", "#173b57", clearFilter === "cleared")}
              >
                クリア済み
              </button>
              <button
                onClick={() =>
                  onBookmarkFilterChange(bookmarkFilter === "bookmarked" ? "all" : "bookmarked")
                }
                style={headerChipStyle("#fff7d6", "#8a6a00", bookmarkFilter === "bookmarked")}
              >
                ブックマーク
              </button>
              <button
                onClick={() => {
                  const btn = document.getElementById("randomPuzzleTrigger");
                  if (btn) btn.click();
                }}
                style={headerChipStyle("#ffffff", "#173b57", false)}
              >
                ランダム
              </button>
              <button
                onClick={onRandomUncleared}
                style={headerChipStyle("#ffffff", "#173b57", false)}
              >
                ランダム未クリア
              </button>
              <button
                onClick={onOpenNumberList}
                style={headerChipStyle("#ffffff", "#173b57", false)}
              >
                問題番号一覧
              </button>
            </div>

            <div
              style={{
                height: 1,
                background: "rgba(255,255,255,0.14)",
              }}
            />

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                onClick={() => onFilterChange("latest")}
                style={headerChipStyle("#f5c542", "#111", activeFilter === "latest")}
              >
                最新
              </button>
              <button
                onClick={() => onFilterChange(1)}
                style={headerChipStyle("#2b95ff", "white", activeFilter === 1)}
              >
                難易度1
              </button>
              <button
                onClick={() => onFilterChange(2)}
                style={headerChipStyle("#ff4fa3", "white", activeFilter === 2)}
              >
                難易度2
              </button>
              <button
                onClick={() => onFilterChange(3)}
                style={headerChipStyle("#7ed321", "white", activeFilter === 3)}
              >
                難易度3
              </button>
              <button
                onClick={() => onFilterChange(4)}
                style={headerChipStyle("#ff9f1c", "white", activeFilter === 4)}
              >
                難易度4
              </button>
              <button
                onClick={() => onFilterChange(5)}
                style={headerChipStyle("#ff5252", "white", activeFilter === 5)}
              >
                難易度5
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}


function headerChipStyle(
  backgroundColor: string,
  color: string,
  active = false
): React.CSSProperties {
  return {
    backgroundColor,
    color,
    border: active ? "2px solid rgba(255,255,255,0.95)" : "1px solid rgba(0,0,0,0.06)",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    opacity: active ? 1 : 0.98,
    boxShadow: active ? "0 8px 20px rgba(0,0,0,0.18)" : "0 4px 12px rgba(0,0,0,0.10)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease",
    transform: active ? "translateY(-1px)" : "translateY(0)",
    whiteSpace: "nowrap",
    minHeight: 40,
    flexShrink: 0,
  };
}
function normalizeAnswerText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[ァ-ヶ]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - 0x60)
    )
    .replace(/\s+/g, "")
    .replace(/　/g, "");
}

function getAcceptedAnswers(puzzle: PuzzleStep): string[] {
  const candidates = Array.isArray(puzzle.answerCandidates)
    ? puzzle.answerCandidates
    : [];

  const legacyAnswer = puzzle.answer ? [puzzle.answer] : [];

  return [...candidates, ...legacyAnswer]
    .map((answer) => answer.trim())
    .filter((answer, index, self) => answer.length > 0 && self.indexOf(answer) === index)
    .slice(0, 5);
}

function getPuzzleSteps(puzzle: Puzzle): PuzzleStep[] {
  if (Array.isArray(puzzle.steps) && puzzle.steps.length > 0) return puzzle.steps;
  return [{
    imageUrl: puzzle.imageUrl,
    answer: puzzle.answer,
    answerCandidates: puzzle.answerCandidates,
    answerFormat: puzzle.answerFormat,
    hint: puzzle.hint,
  }];
}

function isPuzzlePublished(puzzle: Puzzle): boolean {
  return puzzle.isVisible !== false;
}

const HomePage: React.FC = () => {
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [selected, setSelected] = useState<Puzzle | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [input, setInput] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<number | "latest">("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [user, setUser] = useState<User | null>(null);
  const [clearedIds, setClearedIds] = useState<string[]>([]);
  const [bookmarkedIds, setBookmarkedIds] = useState<string[]>([]);
  const [clearFilter, setClearFilter] = useState<"all" | "uncleared" | "cleared">("all");
  const [bookmarkFilter, setBookmarkFilter] = useState<"all" | "bookmarked">("all");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [isHintOpen, setIsHintOpen] = useState(false);
  const [showCorrectBurst, setShowCorrectBurst] = useState(false);
  const [isNumberListOpen, setIsNumberListOpen] = useState(false);
  const [isDesktopLayout, setIsDesktopLayout] = useState(false);
  const correctBurstTimerRef = useRef<number | null>(null);
  const stepRefs = useRef<Array<HTMLDivElement | null>>([]);
  const puzzleDetailRef = useRef<HTMLDivElement | null>(null);
  const puzzleHeadingRef = useRef<HTMLDivElement | null>(null);
  const itemsPerPage = isDesktopLayout ? 28 : 30;
  const selectedLevel = selected?.level ?? 0;
  const isCorrect = result === "正解！";
  const selectedSteps = selected ? getPuzzleSteps(selected) : [];

  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(collection(db, "puzzles"));
      const list = snap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as any) }) as Puzzle
      );
      setPuzzles(list);
      console.log("Loaded puzzles:", list);
    };
    load();
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("clearedPuzzles");
    if (saved) setClearedIds(JSON.parse(saved));
    const bookmarked = localStorage.getItem("bookmarkedPuzzles");
    if (bookmarked) setBookmarkedIds(JSON.parse(bookmarked));
  }, []);

  useEffect(() => {
    return () => {
      if (correctBurstTimerRef.current !== null) {
        window.clearTimeout(correctBurstTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const updateLayout = () => {
      setIsDesktopLayout(window.innerWidth >= 900);
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);

    return () => {
      window.removeEventListener("resize", updateLayout);
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [levelFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, clearFilter, bookmarkFilter]);

  useEffect(() => {
    if (currentStep === 0) return;
    stepRefs.current[currentStep]?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentStep]);

  useEffect(() => {
    if (!selected) return;
    const detail = puzzleDetailRef.current;
    if (!detail) return;

    const image = detail.querySelector("img");
    let frame = 0;
    const positionPuzzle = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const target = puzzleHeadingRef.current ?? detail;
        target.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start",
          inline: "nearest",
        });
      });
    };

    // Wait for the image to load so the page has enough height to reach the heading.
    if (!image || image.complete) {
      positionPuzzle();
    } else {
      image.addEventListener("load", positionPuzzle);
      image.addEventListener("error", positionPuzzle);
    }

    return () => {
      cancelAnimationFrame(frame);
      image?.removeEventListener("load", positionPuzzle);
      image?.removeEventListener("error", positionPuzzle);
    };
  }, [selected]);

    useEffect(() => {
    if (puzzles.length === 0 || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const puzzleId = params.get("puzzle");
    if (!puzzleId) return;

    const targetPuzzle = puzzles.find((p) => p.id === puzzleId && isPuzzlePublished(p));
    if (!targetPuzzle) {
      setSelected(null);
      return;
    }

    setSelected(targetPuzzle);
    setCurrentStep(0);
    setInput("");
    setResult(null);
    setIsHintOpen(false);
    setShowCorrectBurst(false);
  }, [puzzles]);

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const toggleBookmark = (puzzleId: string) => {
    setBookmarkedIds((prev) => {
      const updated = prev.includes(puzzleId)
        ? prev.filter((id) => id !== puzzleId)
        : [...prev, puzzleId];
      localStorage.setItem("bookmarkedPuzzles", JSON.stringify(updated));
      return updated;
    });
  };

  const filteredPuzzles = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    const visibleBase = puzzles.filter(isPuzzlePublished);

    const base =
      levelFilter === "latest"
        ? [...visibleBase]
        : visibleBase.filter((p) => p.level === levelFilter);
    const searched = normalizedSearch
      ? base.filter((p) => {
          const idText = p.id.toLowerCase();
          const titleText = p.title.toLowerCase();
          return idText.includes(normalizedSearch) || titleText.includes(normalizedSearch);
        })
      : base;

    const clearFiltered = searched.filter((p) => {
      if (clearFilter === "uncleared") return !clearedIds.includes(p.id);
      if (clearFilter === "cleared") return clearedIds.includes(p.id);
      return true;
    });

    const bookmarkFiltered = clearFiltered.filter((p) => {
      if (bookmarkFilter === "bookmarked") return bookmarkedIds.includes(p.id);
      return true;
    });

    return bookmarkFiltered.sort((a, b) => {
      const aNum = parseInt(a.id.replace(/\D/g, "")) || 0;
      const bNum = parseInt(b.id.replace(/\D/g, "")) || 0;
      return bNum - aNum;
    });
  }, [puzzles, levelFilter, searchText, clearFilter, bookmarkFilter, clearedIds, bookmarkedIds]);

  const totalPages = Math.max(1, Math.ceil(filteredPuzzles.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const visiblePuzzles = filteredPuzzles.slice(startIndex, startIndex + itemsPerPage);
  const publishedPuzzles = useMemo(
    () => puzzles
      .filter(isPuzzlePublished)
      .sort((a, b) => a.id.localeCompare(b.id, "ja", { numeric: true })),
    [puzzles]
  );
  const visiblePuzzleCount = filteredPuzzles.length;
  const clearedPuzzleCount = filteredPuzzles.filter((p) => clearedIds.includes(p.id)).length;
  const clearRate = visiblePuzzleCount > 0 ? (clearedPuzzleCount / visiblePuzzleCount) * 100 : 0;
  const clearRateText = clearRate.toFixed(2);
  const selectedPuzzleIndex = selected && isPuzzlePublished(selected)
    ? filteredPuzzles.findIndex((p) => p.id === selected.id)
    : -1;
  const previousPuzzle =
    selectedPuzzleIndex > 0 ? filteredPuzzles[selectedPuzzleIndex - 1] : null;
  const nextPuzzle =
    selectedPuzzleIndex >= 0 && selectedPuzzleIndex < filteredPuzzles.length - 1
      ? filteredPuzzles[selectedPuzzleIndex + 1]
      : null;

  const checkAnswer = () => {
    if (!selected) return;

    const normalizedInput = normalizeAnswerText(input);
    const steps = getPuzzleSteps(selected);
    const acceptedAnswers = getAcceptedAnswers(steps[currentStep]);
    const isMatched = acceptedAnswers.some(
      (answer) => normalizeAnswerText(answer) === normalizedInput
    );

    if (isMatched) {
      if (currentStep < steps.length - 1) {
        setCurrentStep((step) => step + 1);
        setInput("");
        setResult(null);
        setIsHintOpen(false);
        setShowCorrectBurst(false);
        return;
      }

      setResult("正解！");
      setShowCorrectBurst(true);
      if (correctBurstTimerRef.current !== null) {
        window.clearTimeout(correctBurstTimerRef.current);
      }
      correctBurstTimerRef.current = window.setTimeout(() => {
        setShowCorrectBurst(false);
      }, 1600);

      if (!clearedIds.includes(selected.id)) {
        const updated = [...clearedIds, selected.id];
        setClearedIds(updated);
        localStorage.setItem("clearedPuzzles", JSON.stringify(updated));
      }
    } else {
      setShowCorrectBurst(false);
      setResult("不正解");
    }
  };

  const openRandomPuzzle = () => {
    if (filteredPuzzles.length === 0) return;

    const random = filteredPuzzles[Math.floor(Math.random() * filteredPuzzles.length)];
    openPuzzleDetail(random);
  };

  const openRandomUnclearedPuzzle = () => {
    const uncleared = filteredPuzzles.filter((p) => !clearedIds.includes(p.id));
    if (uncleared.length === 0) return;

    const random = uncleared[Math.floor(Math.random() * uncleared.length)];
    openPuzzleDetail(random);
  };

const openPuzzleDetail = (puzzle: Puzzle | null) => {
  if (!puzzle) return;
  setSelected(puzzle);
  setCurrentStep(0);
  setInput("");
  setResult(null);
  setIsHintOpen(false);
  setShowCorrectBurst(false);
  setIsNumberListOpen(false);

    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("puzzle", puzzle.id);
      window.history.replaceState({}, "", url.toString());
    }
  };

  const handleSharePuzzleToX = () => {
    if (!selected) return;

    const shareUrl = `${window.location.origin}/share/${selected.id}`;
    const shareText = `No.${selected.id} 『${selected.title}』 #dachou謎`;

    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    const appUrl = `twitter://post?message=${encodedText}%0A${encodedUrl}`;
    const webUrl = `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}`;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isXInAppBrowser =
      userAgent.includes("twitter") ||
      userAgent.includes("x client") ||
      userAgent.includes("xapp");

    if (isXInAppBrowser) {
      window.location.href = appUrl;
      return;
    }

    const fallbackTimer = window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        window.open(webUrl, "_blank", "noopener,noreferrer");
      }
    }, 1200);

    const clearFallback = () => {
      window.clearTimeout(fallbackTimer);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearFallback();
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };

    window.addEventListener("pagehide", clearFallback, { once: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.location.href = appUrl;
  };
  const handleShareToX = () => {
    if (!selected || !isCorrect) return;

    const shareUrl = `${window.location.origin}/share/${selected.id}`;
    const shareText = `No.${selected.id}『${selected.title}』に挑戦！\nあなたも解ける？\n制作:dachou(@dachou_nazo)\n#dachou謎 #謎解き`;

    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    const appUrl = `twitter://post?message=${encodedText}%0A${encodedUrl}`;
    const webUrl = `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}`;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isXInAppBrowser =
      userAgent.includes("twitter") ||
      userAgent.includes("x client") ||
      userAgent.includes("xapp");

    // Xアプリ内ブラウザからはWebフォールバックを行わない
    // これにより、Xに遷移したあと再びXのブラウザが開くループを防ぐ
    if (isXInAppBrowser) {
      window.location.href = appUrl;
      return;
    }

    const fallbackTimer = window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        window.open(webUrl, "_blank", "noopener,noreferrer");
      }
    }, 1200);

    const clearFallback = () => {
      window.clearTimeout(fallbackTimer);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearFallback();
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };

    window.addEventListener("pagehide", clearFallback, {once: true});
    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.location.href = appUrl;
  };

  return (
    <div
      style={{
        padding: "24px 16px 40px",
        maxWidth: 1180,
        width: "100%",
        margin: "0 auto",
        background: pageBackground,
        minHeight: "100vh",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <SiteHeader
        activeFilter={levelFilter}
        onFilterChange={setLevelFilter}
        searchText={searchText}
        onSearchChange={setSearchText}
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        clearFilter={clearFilter}
        bookmarkFilter={bookmarkFilter}
        onClearFilterChange={setClearFilter}
        onBookmarkFilterChange={setBookmarkFilter}
        onRandomUncleared={openRandomUnclearedPuzzle}
        onOpenNumberList={() => setIsNumberListOpen(true)}
      />
      <div
        style={{
          background: "white",
          borderRadius: 18,
          padding: "18px 20px",
          border: cardBorder,
          boxShadow: "0 6px 18px rgba(23,59,87,0.08)",
          marginBottom: 18,
          lineHeight: 1.7,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, color: accentColor }}>
          このサイトについて
        </div>
        <div style={{ fontSize: 14, color: "#374151" }}>
          このサイトは無料で遊べる謎解き問題集です。<br />
          初心者から上級者まで楽しめます。
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 8,
          marginBottom: 18,
        }}
      >
      <div
        style={{
          background: "white",
          borderRadius: 16,
          padding: "14px 12px",
          border: cardBorder,
          boxShadow: "0 4px 16px rgba(23,59,87,0.06)",
          fontWeight: 700,
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 4, whiteSpace: "nowrap" }}>
          表示中
        </div>
        <div style={{ fontSize: 16, color: "#111827", fontWeight: 800, whiteSpace: "nowrap" }}>
          {filteredPuzzles.length} 問
        </div>
      </div>
      <div
        style={{
          background: "white",
          borderRadius: 16,
          padding: "14px 12px",
          border: cardBorder,
          boxShadow: "0 4px 16px rgba(23,59,87,0.06)",
          fontWeight: 700,
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 4, whiteSpace: "nowrap" }}>
          クリア済み
        </div>
        <div style={{ fontSize: 16, color: "#111827", fontWeight: 800, whiteSpace: "nowrap" }}>
          {clearedPuzzleCount} 問
        </div>
      </div>
      <div
        style={{
          background: "white",
          borderRadius: 16,
          padding: "14px 12px",
          border: cardBorder,
          boxShadow: "0 4px 16px rgba(23,59,87,0.06)",
          fontWeight: 700,
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 4, whiteSpace: "nowrap" }}>
          ブックマーク
        </div>
        <div style={{ fontSize: 16, color: "#111827", fontWeight: 800, whiteSpace: "nowrap" }}>
          {bookmarkedIds.length} 問
        </div>
      </div>
      </div>
      <button
        id="randomPuzzleTrigger"
        style={{ display: "none" }}
        onClick={openRandomPuzzle}
      />
      {isNumberListOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1000,
          }}
          onClick={() => setIsNumberListOpen(false)}
        >
          <div
            style={{
              width: "min(760px, calc(100vw - 24px))",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "white",
              borderRadius: 24,
              border: cardBorder,
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
              padding: "20px 16px 16px",
              boxSizing: "border-box",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
                flexWrap: "wrap",
                position: "relative",
              }}
            >
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: accentColor }}>
                  問題番号一覧
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                  解けた問題は緑色、未クリアの問題は黒色で表示されます
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsNumberListOpen(false)}
              style={{
                position: "absolute",
                top: 0,
                right: 12,
                border: "none",
                background: "#eef4fb",
                color: accentColor,
                fontWeight: 700,
                borderRadius: 999,
                padding: "6px 10px",
                cursor: "pointer",
                boxShadow: "0 4px 10px rgba(23,59,87,0.12)",
              }}
            >
              ×
            </button>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  background: "#f8fbff",
                  border: cardBorder,
                  borderRadius: 16,
                  padding: "14px 16px",
                  boxSizing: "border-box",
                }}
              >
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 700 }}>
                  正解数
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: accentColor }}>
                  {clearedPuzzleCount}
                </div>
              </div>
              <div
                style={{
                  background: "#f8fbff",
                  border: cardBorder,
                  borderRadius: 16,
                  padding: "14px 16px",
                  boxSizing: "border-box",
                }}
              >
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 700 }}>
                  全問題数
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: accentColor }}>
                  {visiblePuzzleCount}
                </div>
              </div>
              <div
                style={{
                  background: "#f8fbff",
                  border: cardBorder,
                  borderRadius: 16,
                  padding: "14px 16px",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 700 }}>
                    クリア率
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: accentColor }}>
                    {clearRateText}%
                  </div>
                </div>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: `conic-gradient(#137333 0deg ${clearRate * 3.6}deg, #dbe5ef ${clearRate * 3.6}deg 360deg)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "#f8fbff",
                      border: "1px solid #d8e1eb",
                    }}
                  />
                </div>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
                gap: 8,
              }}
            >
            {publishedPuzzles.map((p) => {
                const isCleared = clearedIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => openPuzzleDetail(p)}
                    style={{
                      border: isCleared ? "1px solid #b7e1c1" : "1px solid #d8e1eb",
                      background: isCleared ? "#edf9f0" : "#ffffff",
                      color: isCleared ? "#137333" : "#111827",
                      borderRadius: 12,
                      padding: "10px 8px",
                      fontWeight: 800,
                      fontSize: 13,
                      cursor: "pointer",
                      boxShadow: isCleared
                        ? "0 6px 14px rgba(19,115,51,0.10)"
                        : "0 4px 10px rgba(23,59,87,0.06)",
                    }}
                  >
                    No.{p.id}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {!selected && (
        <>
          <ul
            className="puzzle-grid"
            style={{
              listStyle: "none",
              padding: 0,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              margin: 0,
            }}
          >
            {visiblePuzzles.map((p) => (
              <li key={p.id}>
                <button
                  className="puzzle-card"
                  onClick={() => {
                    openPuzzleDetail(p);
                  }}
                  onMouseEnter={() => setHoveredId(p.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "space-between",
                    padding: 14,
                    width: "100%",
                    aspectRatio: "1 / 1",
                    backgroundColor: clearedIds.includes(p.id) ? "#f3fbf5" : "white",
                    border: clearedIds.includes(p.id) ? "1px solid #bfe3c8" : cardBorder,
                    borderRadius: 18,
                    cursor: "pointer",
                    boxShadow:
                      hoveredId === p.id
                        ? "0 16px 34px rgba(23,59,87,0.16)"
                        : "0 8px 22px rgba(23,59,87,0.08)",
                    textAlign: "left",
                    transition:
                      "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background-color 0.18s ease",
                    transform: hoveredId === p.id ? "translateY(-4px)" : "translateY(0)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBookmark(p.id);
                    }}
                    aria-label={bookmarkedIds.includes(p.id) ? "ブックマークを外す" : "ブックマークする"}
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      width: 38,
                      height: 38,
                      borderRadius: 999,
                      border: "1px solid rgba(23,59,87,0.12)",
                      background: "rgba(255,255,255,0.96)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      cursor: "pointer",
                      zIndex: 2,
                      boxShadow: "0 6px 14px rgba(23,59,87,0.12)",
                    }}
                  >
                    <span style={{ color: bookmarkedIds.includes(p.id) ? "#f5c542" : "#94a3b8" }}>
                      {bookmarkedIds.includes(p.id) ? "★" : "☆"}
                    </span>
                  </button>
                  {p.imageUrl && (
                    <img
                      className="puzzle-card-image"
                      src={p.imageUrl}
                      alt={p.title}
                      style={{
                        width: "100%",
                        height: "76%",
                        objectFit: "contain",
                        borderRadius: 12,
                        background: "#f8fbff",
                      }}
                    />
                  )}
                  <div style={{ marginTop: 10, textAlign: "center", position: "relative" }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: goldColor,
                        letterSpacing: "0.08em",
                        minHeight: 20,
                      }}
                    >
                      {Array.from({ length: 5 }, (_, i) => (
                        <span key={i} style={{ opacity: i < (p.level ?? 0) ? 1 : 0.22 }}>
                          ★
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>No.{p.id}</div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        lineHeight: 1.45,
                        marginTop: 4,
                        color: "#1f2937",
                      }}
                    >
                      『{p.title}』
                    </div>
                    {bookmarkedIds.includes(p.id) && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#9a7600",
                          fontWeight: 700,
                          marginTop: 6,
                        }}
                      >
                        ★ ブックマーク済み
                      </div>
                    )}
                    {clearedIds.includes(p.id) && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            top: -4,
                            right: -2,
                            background: "#137333",
                            color: "white",
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "6px 10px",
                            borderRadius: 999,
                            boxShadow: "0 4px 12px rgba(19,115,51,0.22)",
                          }}
                        >
                          CLEAR
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#137333",
                            fontWeight: 700,
                            marginTop: 6,
                          }}
                        >
                          ✔ クリア済み
                        </div>
                      </>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {visiblePuzzles.length === 0 && (
            <p
              style={{
                textAlign: "center",
                marginTop: 24,
                fontWeight: 700,
                background: "white",
                border: cardBorder,
                borderRadius: 16,
                padding: "22px 16px",
                color: "#374151",
              }}
            >
              条件に一致する問題がありません
            </p>
          )}
          {visiblePuzzles.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                alignItems: "center",
                gap: 8,
                marginTop: 28,
                background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                border: cardBorder,
                borderRadius: 20,
                padding: "14px 12px",
                boxShadow: "0 10px 24px rgba(23,59,87,0.08)",
              }}
            >
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: "11px 8px",
                  borderRadius: 999,
                  border: currentPage === 1 ? "1px solid #e5e7eb" : "1px solid #d7e3ef",
                  background: currentPage === 1 ? "#f3f5f7" : "#ffffff",
                  cursor: currentPage === 1 ? "default" : "pointer",
                  fontWeight: 800,
                  color: currentPage === 1 ? "#9ca3af" : accentColor,
                  minWidth: 0,
                  width: "100%",
                  fontSize: "clamp(12px, 2.8vw, 16px)",
                  whiteSpace: "nowrap",
                  boxShadow: currentPage === 1 ? "none" : "0 4px 10px rgba(23,59,87,0.08)",
                }}
              >
                ≪ 最初
              </button>

              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "11px 8px",
                  borderRadius: 999,
                  border: currentPage === 1 ? "1px solid #e5e7eb" : "1px solid #d7e3ef",
                  background: currentPage === 1 ? "#f3f5f7" : "#ffffff",
                  cursor: currentPage === 1 ? "default" : "pointer",
                  fontWeight: 800,
                  color: currentPage === 1 ? "#9ca3af" : accentColor,
                  minWidth: 0,
                  width: "100%",
                  fontSize: "clamp(12px, 2.8vw, 16px)",
                  whiteSpace: "nowrap",
                  boxShadow: currentPage === 1 ? "none" : "0 4px 10px rgba(23,59,87,0.08)",
                }}
              >
                ← 前へ
              </button>

              <div
                style={{
                  padding: "10px 8px",
                  borderRadius: 999,
                  background: "linear-gradient(135deg, #173b57 0%, #1e567f 100%)",
                  color: "white",
                  fontWeight: 800,
                  letterSpacing: "0.03em",
                  boxShadow: "0 8px 18px rgba(23,59,87,0.18)",
                  width: "100%",
                  textAlign: "center",
                  fontSize: "clamp(12px, 3vw, 18px)",
                  whiteSpace: "nowrap",
                  boxSizing: "border-box",
                }}
              >
                {currentPage} / {totalPages}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: "11px 8px",
                  borderRadius: 999,
                  border: currentPage === totalPages ? "1px solid #e5e7eb" : "1px solid #d7e3ef",
                  background: currentPage === totalPages ? "#f3f5f7" : "#ffffff",
                  cursor: currentPage === totalPages ? "default" : "pointer",
                  fontWeight: 800,
                  color: currentPage === totalPages ? "#9ca3af" : accentColor,
                  minWidth: 0,
                  width: "100%",
                  fontSize: "clamp(12px, 2.8vw, 16px)",
                  whiteSpace: "nowrap",
                  boxShadow: currentPage === totalPages ? "none" : "0 4px 10px rgba(23,59,87,0.08)",
                }}
              >
                次へ →
              </button>

              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "11px 8px",
                  borderRadius: 999,
                  border: currentPage === totalPages ? "1px solid #e5e7eb" : "1px solid #d7e3ef",
                  background: currentPage === totalPages ? "#f3f5f7" : "#ffffff",
                  cursor: currentPage === totalPages ? "default" : "pointer",
                  fontWeight: 800,
                  color: currentPage === totalPages ? "#9ca3af" : accentColor,
                  minWidth: 0,
                  width: "100%",
                  fontSize: "clamp(12px, 2.8vw, 16px)",
                  whiteSpace: "nowrap",
                  boxShadow: currentPage === totalPages ? "none" : "0 4px 10px rgba(23,59,87,0.08)",
                }}
              >
                最後 ≫
              </button>
            </div>
          )}
        </>
      )}

      {selected && (
        <div
          key={selected.id}
          ref={puzzleDetailRef}
          style={{
            background: surfaceColor,
            borderRadius: 28,
            border: cardBorder,
            boxShadow: "0 18px 40px rgba(23,59,87,0.10)",
            padding: 28,
            backdropFilter: "blur(10px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {showCorrectBurst && (
            <>
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  background: "radial-gradient(circle, rgba(245,197,66,0.18) 0%, rgba(245,197,66,0) 62%)",
                  animation: "correctFlash 1.2s ease-out forwards",
                }}
              />
              {[
                { top: "10%", left: "14%", delay: "0s" },
                { top: "18%", left: "78%", delay: "0.08s" },
                { top: "36%", left: "8%", delay: "0.16s" },
                { top: "38%", left: "86%", delay: "0.24s" },
                { top: "68%", left: "16%", delay: "0.12s" },
                { top: "72%", left: "80%", delay: "0.2s" },
                { top: "84%", left: "42%", delay: "0.28s" },
                { top: "14%", left: "46%", delay: "0.04s" },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    position: "absolute",
                    top: item.top,
                    left: item.left,
                    fontSize: 28,
                    lineHeight: 1,
                    pointerEvents: "none",
                    animation: `correctConfetti 1.2s ease-out ${item.delay} forwards`,
                  }}
                >
                  ✨
                </div>
              ))}
              <div
                style={{
                  position: "absolute",
                  top: 18,
                  right: 18,
                  padding: "10px 16px",
                  borderRadius: 999,
                  background: "linear-gradient(135deg, #f5c542 0%, #ffd86b 100%)",
                  color: "#533600",
                  fontWeight: 800,
                  boxShadow: "0 12px 26px rgba(245,197,66,0.32)",
                  pointerEvents: "none",
                  animation: "correctBadge 1.1s ease-out forwards",
                }}
              >
                CLEAR!
              </div>
            </>
          )}
          <button
            onClick={() => {
              setSelected(null);
              if (typeof window !== "undefined") {
                const url = new URL(window.location.href);
                url.searchParams.delete("puzzle");
                window.history.replaceState({}, "", url.toString());
              }
            }}
            style={{
              border: "none",
              background: "#eef4fb",
              color: accentColor,
              fontWeight: 700,
              borderRadius: 999,
              padding: "10px 16px",
              cursor: "pointer",
              marginBottom: 18,
            }}
          >
            ← 問題一覧に戻る
          </button>

          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div
              ref={puzzleHeadingRef}
              style={{ scrollMarginTop: "calc(56px + env(safe-area-inset-top, 0px))" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginBottom: 8 }}>
                    No.{selected.id}
                  </div>
                  <h2 style={{ margin: 0, fontSize: 30, color: "#142535", lineHeight: 1.3 }}>
                    {selected.title}
                  </h2>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleBookmark(selected.id)}
                    style={{
                      border: "1px solid #d8e1eb",
                      background: bookmarkedIds.includes(selected.id) ? "#fff7d6" : "white",
                      color: bookmarkedIds.includes(selected.id) ? "#8a6a00" : accentColor,
                      fontWeight: 700,
                      borderRadius: 999,
                      padding: "10px 14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span>{bookmarkedIds.includes(selected.id) ? "★" : "☆"}</span>
                    <span>{bookmarkedIds.includes(selected.id) ? "ブックマーク済み" : "ブックマーク"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSharePuzzleToX}
                    style={{
                      border: "1px solid #d8e1eb",
                      background: "#111827",
                      color: "white",
                      fontWeight: 700,
                      borderRadius: 999,
                      padding: "10px 14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      minHeight: 40,
                    }}
                  >
                    <img
                      src="/logo-white.png?v=2"
                      alt="X"
                      style={{ width: 18, height: 18, objectFit: "contain" }}
                    />
                    <span>シェアする</span>
                  </button>
                </div>
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 18,
                  color: goldColor,
                  letterSpacing: "0.12em",
                }}
              >
                {Array.from({ length: 5 }, (_, i) => (
                  <span key={i} style={{ opacity: i < selectedLevel ? 1 : 0.22 }}>
                    ★
                  </span>
                ))}
              </div>
            </div>

            {selectedSteps.length === 1 && (
              <>
            {selected.imageUrl && (
              <div
                style={{
                  background: "#f8fbff",
                  borderRadius: 20,
                  border: cardBorder,
                  padding: 16,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={selected.imageUrl}
                  alt={selected.title}
                  style={{
                    maxWidth: "100%",
                    maxHeight: 520,
                    width: "auto",
                    borderRadius: 12,
                  }}
                />
              </div>
            )}

            <div
              style={{
                background: "#f8fbff",
                border: cardBorder,
                borderRadius: 18,
                padding: 18,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6, color: "#22384b" }}>
                答えを入力
              </div>
              {selected.answerFormat && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: "#eef4fb",
                    color: accentColor,
                    fontSize: 13,
                    fontWeight: 700,
                    marginBottom: 10,
                  }}
                >
                  <span>推奨解答方法</span>
                  <span>{selected.answerFormat}</span>
                </div>
              )}
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                ひらがな、カタカナの表記揺れは自動で処理されます。アルファベットの大文字、小文字の区別はしません。空白は無視されます。
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="答えを入力"
                  style={{
                    flex: "1 1 280px",
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid #cfd8e3",
                    fontSize: 16,
                    boxSizing: "border-box",
                  }}
                />
                <button
                  onClick={checkAnswer}
                  style={{
                    border: "none",
                    background: accentColor,
                    color: "white",
                    fontWeight: 700,
                    borderRadius: 12,
                    padding: "12px 18px",
                    cursor: "pointer",
                  }}
                >
                  判定する
                </button>
              </div>
              {result && (
                <div
                  style={{
                    marginTop: 14,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      fontWeight: 700,
                      background: isCorrect ? "#edf9f0" : "#fff1f1",
                      color: isCorrect ? "#137333" : "#c62828",
                      border: isCorrect ? "1px solid #b7e1c1" : "1px solid #f2b8b5",
                    }}
                  >
                    {result}
                  </div>

                  {isCorrect && (
                    <button
                      type="button"
                      onClick={handleShareToX}
                      style={{
                        border: "none",
                        background: "#111827",
                        color: "white",
                        fontWeight: 700,
                        borderRadius: 12,
                        padding: "12px 16px",
                        cursor: "pointer",
                        alignSelf: "flex-start",
                        boxShadow: "0 8px 18px rgba(17,24,39,0.18)",
                      }}
                    >
                      Xにポストする
                    </button>
                  )}
                </div>
              )}
            </div>

            {selected.hint && (
              <div
                style={{
                  background: "#fff9e8",
                  border: "1px solid #f2de91",
                  borderRadius: 18,
                  padding: 18,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#735400" }}>ヒント</div>
                  <button
                    onClick={() => setIsHintOpen((prev) => !prev)}
                    style={{
                      border: "none",
                      background: "#f5c542",
                      color: "#3d2d00",
                      fontWeight: 700,
                      borderRadius: 999,
                      padding: "10px 14px",
                      cursor: "pointer",
                    }}
                  >
                    {isHintOpen ? "ヒントを閉じる" : "ヒントを表示"}
                  </button>
                </div>
                {isHintOpen ? (
                  <div style={{ lineHeight: 1.7, color: "#5b4b00", marginTop: 12 }}>
                    {selected.hint}
                  </div>
                ) : (
                  <div style={{ lineHeight: 1.7, color: "#8a6d1f", marginTop: 12 }}>
                    ヒントは非表示です。必要なときだけ開いて確認できます。
                  </div>
                )}
              </div>
            )}
              </>
            )}

            {selectedSteps.length > 1 && selectedSteps.slice(0, currentStep + 1).map((step, index) => {
              const isCurrentStep = index === currentStep;
              return (
                <div
                  key={index}
                  ref={(element) => { stepRefs.current[index] = element; }}
                  style={{
                    scrollMarginTop: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    paddingBottom: index < currentStep ? 20 : 0,
                    borderBottom: index < currentStep ? "1px solid #d8e1eb" : "none",
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 800, color: accentColor }}>
                    問題 {index + 1} / {selectedSteps.length}
                  </div>

                  {step.imageUrl && (
                    <div style={{ background: "#f8fbff", borderRadius: 20, border: cardBorder, padding: 16, display: "flex", justifyContent: "center" }}>
                      <img
                        src={step.imageUrl}
                        alt={`${selected.title} 問題${index + 1}`}
                        style={{ maxWidth: "100%", maxHeight: 520, width: "auto", borderRadius: 12 }}
                      />
                    </div>
                  )}

                  {isCurrentStep ? (
                    <div style={{ background: "#f8fbff", border: cardBorder, borderRadius: 18, padding: 18 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: "#22384b" }}>答えを入力</div>
                      {step.answerFormat && (
                        <div style={{ display: "inline-flex", padding: "8px 12px", borderRadius: 999, background: "#eef4fb", color: accentColor, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
                          推奨解答方法：{step.answerFormat}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                        ひらがな・カタカナ、英字の大小、空白の表記揺れは自動で処理されます。
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <input
                          value={input}
                          onChange={(e) => { setInput(e.target.value); if (result === "不正解") setResult(null); }}
                          onKeyDown={(e) => { if (e.key === "Enter") checkAnswer(); }}
                          placeholder="答えを入力"
                          disabled={isCorrect}
                          style={{ flex: "1 1 280px", padding: "12px 14px", borderRadius: 12, border: "1px solid #cfd8e3", fontSize: 16, boxSizing: "border-box" }}
                        />
                        <button
                          onClick={checkAnswer}
                          disabled={isCorrect}
                          style={{ border: "none", background: accentColor, color: "white", fontWeight: 700, borderRadius: 12, padding: "12px 18px", cursor: isCorrect ? "default" : "pointer", opacity: isCorrect ? 0.6 : 1 }}
                        >
                          判定する
                        </button>
                      </div>
                      {result && (
                        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                          <div style={{ padding: "12px 14px", borderRadius: 12, fontWeight: 700, background: isCorrect ? "#edf9f0" : "#fff1f1", color: isCorrect ? "#137333" : "#c62828", border: isCorrect ? "1px solid #b7e1c1" : "1px solid #f2b8b5" }}>
                            {result}
                          </div>
                          {isCorrect && (
                            <button type="button" onClick={handleShareToX} style={{ border: "none", background: "#111827", color: "white", fontWeight: 700, borderRadius: 12, padding: "12px 16px", cursor: "pointer", alignSelf: "flex-start" }}>
                              Xにポストする
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ padding: "12px 14px", borderRadius: 12, fontWeight: 700, background: "#edf9f0", color: "#137333", border: "1px solid #b7e1c1" }}>
                      正解！
                    </div>
                  )}

                  {step.hint && isCurrentStep && (
                    <div style={{ background: "#fff9e8", border: "1px solid #f2de91", borderRadius: 18, padding: 18 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, color: "#735400" }}>ヒント</div>
                        <button onClick={() => setIsHintOpen((open) => !open)} style={{ border: "none", background: "#f5c542", color: "#3d2d00", fontWeight: 700, borderRadius: 999, padding: "10px 14px", cursor: "pointer" }}>
                          {isHintOpen ? "ヒントを閉じる" : "ヒントを表示"}
                        </button>
                      </div>
                      <div style={{ lineHeight: 1.7, color: isHintOpen ? "#5b4b00" : "#8a6d1f", marginTop: 12 }}>
                        {isHintOpen ? step.hint : "ヒントは非表示です。必要なときだけ開いて確認できます。"}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {(previousPuzzle || nextPuzzle) && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                {previousPuzzle && (
                  <button
                    type="button"
                    onClick={() => openPuzzleDetail(previousPuzzle)}
                    style={{
                      flex: "1 1 220px",
                      padding: "14px 16px",
                      borderRadius: 16,
                      border: "1px solid #d7e3ef",
                      background: "#ffffff",
                      color: accentColor,
                      fontWeight: 700,
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: "0 8px 18px rgba(23,59,87,0.08)",
                    }}
                  >
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>前の問題</div>
                    <div style={{ fontSize: 15 }}>
                      No.{previousPuzzle.id} 『{previousPuzzle.title}』
                    </div>
                  </button>
                )}

                {nextPuzzle && (
                  <button
                    type="button"
                    onClick={() => openPuzzleDetail(nextPuzzle)}
                    style={{
                      flex: "1 1 220px",
                      padding: "14px 16px",
                      borderRadius: 16,
                      border: "1px solid #d7e3ef",
                      background: "#ffffff",
                      color: accentColor,
                      fontWeight: 700,
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: "0 8px 18px rgba(23,59,87,0.08)",
                    }}
                  >
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>次の問題</div>
                    <div style={{ fontSize: 15 }}>
                      No.{nextPuzzle.id} 『{nextPuzzle.title}』
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <style>
        {`
          @media (min-width: 900px) {
            .puzzle-grid {
              grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
            }

            .puzzle-card {
              aspect-ratio: auto !important;
              min-height: 340px !important;
              overflow: visible !important;
            }

            .puzzle-card-image {
              height: 230px !important;
            }
          }
          @keyframes correctConfetti {
            0% {
              opacity: 0;
              transform: translateY(14px) scale(0.5) rotate(0deg);
            }
            20% {
              opacity: 1;
            }
            100% {
              opacity: 0;
              transform: translateY(-56px) scale(1.2) rotate(18deg);
            }
          }

          @keyframes correctFlash {
            0% {
              opacity: 0;
              transform: scale(0.96);
            }
            20% {
              opacity: 1;
            }
            100% {
              opacity: 0;
              transform: scale(1.08);
            }
          }

          @keyframes correctBadge {
            0% {
              opacity: 0;
              transform: translateY(-10px) scale(0.88);
            }
            18% {
              opacity: 1;
            }
            100% {
              opacity: 0;
              transform: translateY(-22px) scale(1.06);
            }
          }
        `}
      </style>
      <footer
        style={{
          marginTop: 28,
          width: "100%",
          padding: "20px 24px",
          borderRadius: 22,
          background: "linear-gradient(135deg, #173b57 0%, #0f2d44 100%)",
          color: "rgba(255,255,255,0.92)",
          boxShadow: "0 14px 30px rgba(15,45,68,0.14)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              dachouの謎解き集
            </div>
            <div style={{ fontSize: 13, opacity: 0.82, lineHeight: 1.7 }}>
              好きな難易度で探して、解いて、クリア記録を残せる謎解きサイト
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              fontSize: 13,
              opacity: 0.9,
            }}
          >
            <a
              href="/terms"
              style={{
                color: "rgba(255,255,255,0.92)",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              利用規約
            </a>
            <span style={{ opacity: 0.6 }}>|</span>
            <a
              href="/privacy"
              style={{
                color: "rgba(255,255,255,0.92)",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              プライバシーポリシー
            </a>
            <span style={{ opacity: 0.6 }}>|</span>
            <span>© dachou nazotoki collection</span>
          </div>
        </div>
      </footer>
      <button
        type="button"
        aria-label="ページの一番上へ移動"
        title="ページの一番上へ移動"
        onClick={() => window.scrollTo({
          top: 0,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        })}
        style={{
          position: "fixed",
          right: "calc(16px + env(safe-area-inset-right, 0px))",
          top: "calc(20px + env(safe-area-inset-top, 0px))",
          zIndex: 900,
          width: 48,
          height: 48,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.6)",
          background: accentColor,
          color: "white",
          boxShadow: "0 4px 14px rgba(15,45,68,0.28)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 21V9m-5 5 5-5 5 5M5 3h14" />
        </svg>
      </button>
      <button
        type="button"
        aria-label="ページの一番下へ移動"
        title="ページの一番下へ移動"
        onClick={() => window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        })}
        style={{
          position: "fixed",
          right: "calc(16px + env(safe-area-inset-right, 0px))",
          bottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
          zIndex: 900,
          width: 48,
          height: 48,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.6)",
          background: accentColor,
          color: "white",
          boxShadow: "0 4px 14px rgba(15,45,68,0.28)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3v12m-5-5 5 5 5-5M5 21h14" />
        </svg>
      </button>
    </div>
  );
};

export default HomePage;
